import { BUTTON_TYPES, Button } from 'components/Button'
import { TAddBankAccountProps } from './types'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { BTN_TXT } from 'constants/txt'
import { TMoovBlockRef } from './types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Moov, loadMoov } from '@moovio/moov-js'
import { useAppDispatch } from 'redux/createStore'
import { getMoovToken, increasePendingCreationBankAccountsCounter } from './api'
import { setLoading } from 'redux/store/common/slice'
import { useLocation, useNavigate } from 'react-router-dom'
import { OPEN_MOOV_PAYMENT_METHODS_STATE_KEY } from './constants'
import './styles.scss'

export const AddBankAccountButton = ({
  businessDetails,
  onAddBankAccount
}: TAddBankAccountProps) => {
  const blockRef = useRef<TMoovBlockRef>(null)
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const [addBankAccountModalKey, setAddBankAccountModalKey] = useState(
    Math.random()
  )

  const onSuccess = useCallback(async () => {
    if (blockRef.current) {
      blockRef.current.open = false
    }
    if (businessDetails.uuid) {
      await increasePendingCreationBankAccountsCounter(businessDetails.uuid)
    }
    onAddBankAccount?.()
    setAddBankAccountModalKey(Math.random())
  }, [businessDetails.uuid, onAddBankAccount])

  const callMoovDropsForm = useCallback(
    async (moovClient: Moov, accessToken: string) => {
      if (!blockRef.current) return
      try {
        const drops = await moovClient.drops()
        drops.paymentMethods(
          {
            token: accessToken,
            accountID: businessDetails.moov_account_id,
            paymentMethodTypes: ['bankAccount'],
            microDeposits: false,
            onError: async (args) => {
              console.error(args)
            },
            onSuccess,
            onCancel: () => {
              if (blockRef.current) {
                blockRef.current.open = false
              }
            }
          },
          blockRef.current.id
        )
        blockRef.current.open = true
      } catch (error) {
        console.error(error)
      }
    },
    [businessDetails.moov_account_id, onSuccess]
  )

  const refreshMoovToken = useCallback(async () => {
    let moovInstance: Moov | null = null
    let accessToken: string | null = null

    const res = await getMoovToken(businessDetails.moov_account_id)
    if (res.data?.access_token) {
      accessToken = res.data.access_token
      moovInstance = await loadMoov(res.data.access_token)
    }
    return { moovInstance, accessToken }
  }, [businessDetails.moov_account_id])

  const initMoov = useCallback(async () => {
    if (!businessDetails.moov_account_id) return
    try {
      dispatch(setLoading(true))

      const { moovInstance, accessToken } = await refreshMoovToken()

      if (moovInstance && accessToken) {
        await callMoovDropsForm(moovInstance, accessToken)
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setLoading(false))
    }
  }, [
    businessDetails.moov_account_id,
    dispatch,
    refreshMoovToken,
    callMoovDropsForm
  ])

  useEffect(() => {
    const shouldOpenMoovForm = Boolean(
      (location.state as Record<string, boolean> | null)?.[
        OPEN_MOOV_PAYMENT_METHODS_STATE_KEY
      ]
    )
    if (!shouldOpenMoovForm || !businessDetails.moov_account_id) return

    initMoov()
    navigate(location.pathname, { replace: true, state: {} })
  }, [
    businessDetails.moov_account_id,
    initMoov,
    location.pathname,
    location.state,
    navigate
  ])

  return (
    <>
      <Button
        className="contract-categories__add-btn mt-24"
        onClick={initMoov}
        icon={<Plus16 />}
        type={BUTTON_TYPES.PRIMARY}
        upperCase
      >
        {BTN_TXT.ADD_BANK_ACCOUNT}
      </Button>
      <div>
        {/* TODO: think to make it as a type */}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <moov-payment-methods
          ref={blockRef}
          id="moov-payment-methods"
          key={addBankAccountModalKey}
        />
      </div>
    </>
  )
}
